import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Button, ButtonToolbar, Card, CardBody, Col } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { KKuTuIOApi } from '../../../KKuTuIOApi';
import renderMultiSelectField from '../form/MultiSelect';
import allThemes from '../../../containers/themes.json';
import allTypes from '../../../containers/types.json';
import koreanFlags from '../../../containers/KoreanWords/components/flags.json';
import englishFlags from '../../../containers/EnglishWords/components/flags.json';
import KoreanWordDetailEdit from '../../../containers/KoreanWords/components/WordDetailEdit';
import EnglishWordDetailEdit from '../../../containers/EnglishWords/components/WordDetailEdit';
import { BulkWordCheckbox, BulkWordNotice } from './BulkWordCommon';
import BulkWordModifyConfirmModal from './BulkWordModifyConfirmModal';
import 'react-toastify/dist/ReactToastify.css';
import './BulkWord.scss';

const MODES = {
  SETTINGS: 'SETTINGS',
  REPLACE: 'REPLACE'
};

const emptyDetail = () => ({ type: {}, theme: {}, mean: '' });

const BulkWordModifyPage = ({ lang, languageName }) => {
  const navigate = useNavigate();
  const [wordText, setWordText] = useState('');
  const [mode, setMode] = useState(MODES.SETTINGS);
  const [flags, setFlags] = useState([]);
  const [details, setDetails] = useState([emptyDetail()]);
  const [replaceThemeFrom, setReplaceThemeFrom] = useState(null);
  const [replaceThemeTo, setReplaceThemeTo] = useState(null);
  const [replaceTypeFrom, setReplaceTypeFrom] = useState(null);
  const [replaceTypeTo, setReplaceTypeTo] = useState(null);
  const [isUpdateLogIgnore, setUpdateLogIgnore] = useState(false);
  const [isUpdateLogIncludeDetail, setUpdateLogIncludeDetail] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [request, setRequest] = useState(null);
  const [isOpenModal, setOpenModal] = useState(false);
  const themes = useMemo(() => allThemes.filter(item => item.lang.includes(lang)), [lang]);
  const types = useMemo(() => allTypes.filter(item => item.lang.includes(lang)), [lang]);
  const flagOptions = lang === 'ko' ? koreanFlags : englishFlags;
  const WordDetailEdit = lang === 'ko' ? KoreanWordDetailEdit : EnglishWordDetailEdit;
  const words = wordText.split('\n').map(word => word.trim()).filter(Boolean);

  useEffect(() => {
    const stored = sessionStorage.getItem(`bulk-word-modify-${lang}`);
    if (!stored) return;
    try {
      const selectedWords = JSON.parse(stored);
      if (Array.isArray(selectedWords)) setWordText(selectedWords.join('\n'));
    } catch (error) {
      // 손상된 임시 선택값은 무시하고 직접 입력을 유지합니다.
    }
    sessionStorage.removeItem(`bulk-word-modify-${lang}`);
  }, [lang]);

  const showToast = (level, message) => toast[level](message, {
    position: 'top-center', autoClose: 10000, closeOnClick: true, pauseOnHover: true, draggable: true
  });

  const buildRequest = () => ({
    words,
    mode,
    flags: mode === MODES.SETTINGS ? flags.map(item => Number(item.value)) : [],
    details: mode === MODES.SETTINGS ? details.map(detail => ({
      type: detail.type.value,
      theme: detail.theme.value,
      mean: detail.mean
    })) : [],
    replaceThemeFrom: mode === MODES.REPLACE && replaceThemeFrom ? replaceThemeFrom.value : null,
    replaceThemeTo: mode === MODES.REPLACE && replaceThemeTo ? replaceThemeTo.value : null,
    replaceTypeFrom: mode === MODES.REPLACE && replaceTypeFrom ? replaceTypeFrom.value : null,
    replaceTypeTo: mode === MODES.REPLACE && replaceTypeTo ? replaceTypeTo.value : null,
    updateLogIgnore: isUpdateLogIgnore,
    updateLogIncludeDetail: isUpdateLogIncludeDetail
  });

  const validate = () => {
    if (!words.length) return '수정할 단어를 한 줄에 하나씩 입력해 주세요.';
    if (mode === MODES.SETTINGS) {
      const invalidIndex = details.findIndex(detail => !detail.type.value || !detail.theme.value);
      if (invalidIndex !== -1) return `${invalidIndex + 1}번째 단어 설정의 품사와 주제를 모두 선택해 주세요.`;
      return '';
    }
    if ((replaceThemeFrom && !replaceThemeTo) || (!replaceThemeFrom && replaceThemeTo)) {
      return '주제 치환의 기존 주제와 새 주제를 모두 선택해 주세요.';
    }
    if ((replaceTypeFrom && !replaceTypeTo) || (!replaceTypeFrom && replaceTypeTo)) {
      return '품사 치환의 기존 품사와 새 품사를 모두 선택해 주세요.';
    }
    if (replaceThemeFrom && replaceThemeFrom.value === replaceThemeTo.value) return '기존 주제와 새 주제는 서로 달라야 합니다.';
    if (replaceTypeFrom && replaceTypeFrom.value === replaceTypeTo.value) return '기존 품사와 새 품사는 서로 달라야 합니다.';
    if (!replaceThemeFrom && !replaceTypeFrom) return '주제 또는 품사 치환 조건을 하나 이상 설정해 주세요.';
    return '';
  };

  const handlePreview = () => {
    if (isSaving) return;
    const error = validate();
    if (error) { showToast('error', error); return; }
    const requestData = buildRequest();
    setSaving(true);
    KKuTuIOApi.post(`/words/${lang}/bulk-modify/preview`, requestData)
      .then(({ data }) => {
        if (!data.success) { showToast('error', `${data.resultMessage} (${data.resultCode})`); return; }
        setRequest(requestData);
        setPreview(data.data);
        setOpenModal(true);
      })
      .catch(error => showToast('error', `대량 수정 사전 확인 중 오류가 발생했습니다: ${error.message || error}`))
      .finally(() => setSaving(false));
  };

  useEffect(() => {
    const keyHandler = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handlePreview();
      }
    };
    window.addEventListener('keydown', keyHandler);
    return () => window.removeEventListener('keydown', keyHandler);
  });

  const selectProps = { className: 'react-select', classNamePrefix: 'react-select', isDisabled: isSaving };
  const logIgnoreChanged = () => {
    setUpdateLogIgnore(!isUpdateLogIgnore);
    if (!isUpdateLogIgnore) setUpdateLogIncludeDetail(false);
  };

  return <>
    <ToastContainer position="top-center" autoClose={10000} newestOnTop closeOnClick draggable pauseOnHover/>
    <Col md={12} lg={12}><Card className="bulk-word-page"><CardBody>
      <h1 className="text-center mb-3">{languageName} 단어 대량 수정</h1>
      <BulkWordNotice color="warning" className="mb-4 bulk-word-page__notice">
        변경 대상과 변경 전후 값을 미리보기에서 확인한 다음 적용합니다. Ctrl/⌘+S로 미리보기를 열 수 있습니다.
      </BulkWordNotice>
      <form className="form form--horizontal">
        <div className="form__form-group"><span className="form__form-group-label">수정할 단어</span><div className="form__form-group-field col-9">
          <div className="bulk-word-page__input-wrap"><textarea className="bulk-word-textarea" rows="10" placeholder="한 줄에 한 단어씩 입력하세요."
            value={wordText} onChange={event => setWordText(event.target.value)}/><div className="bulk-word-page__input-meta"><span>목록에서 선택한 단어가 자동으로 입력됩니다.</span>{words.length > 0 && <strong>{words.length}개 입력</strong>}</div></div>
        </div></div>
        <div className="form__form-group"><span className="form__form-group-label">수정 방식</span><div className="form__form-group-field col-9">
          <div className="bulk-word-modify__modes" role="radiogroup" aria-label="대량 수정 방식">
            <button type="button" role="radio" aria-checked={mode === MODES.SETTINGS} className={mode === MODES.SETTINGS ? 'is-active' : ''} onClick={() => setMode(MODES.SETTINGS)}>
              <strong>단어 설정</strong><span>플래그·품사·주제·뜻을 입력한 값으로 모두 덮어씁니다.</span>
            </button>
            <button type="button" role="radio" aria-checked={mode === MODES.REPLACE} className={mode === MODES.REPLACE ? 'is-active' : ''} onClick={() => setMode(MODES.REPLACE)}>
              <strong>주제/품사 치환</strong><span>일치하는 주제나 품사만 다른 값으로 바꿉니다.</span>
            </button>
          </div>
        </div></div>
        {mode === MODES.SETTINGS ? <>
          <div className="form__form-group"><span className="form__form-group-label">플래그</span><div className="form__form-group-field col-9">
            {renderMultiSelectField({ input: { name: 'bulk-modify-flags', value: flags, onChange: value => setFlags(value || []) }, meta: { touched: false, error: '' }, placeholder: '플래그 목록', options: flagOptions })}
          </div></div>
          <div className="form__form-group"><span className="form__form-group-label">단어 설정</span><div className="form__form-group-field col-9">
            <WordDetailEdit details={details} onChangeDetails={setDetails}/>
          </div></div>
        </> : <>
          <div className="form__form-group"><span className="form__form-group-label">주제 치환</span><div className="form__form-group-field col-9 bulk-word-modify__pair">
            <Select {...selectProps} isClearable value={replaceThemeFrom} options={themes} placeholder="기존 주제" onChange={setReplaceThemeFrom}/>
            <Select {...selectProps} isClearable value={replaceThemeTo} options={themes} placeholder="새 주제" onChange={setReplaceThemeTo}/>
          </div></div>
          <div className="form__form-group"><span className="form__form-group-label">품사 치환</span><div className="form__form-group-field col-9 bulk-word-modify__pair">
            <Select {...selectProps} isClearable value={replaceTypeFrom} options={types} placeholder="기존 품사" onChange={setReplaceTypeFrom}/>
            <Select {...selectProps} isClearable value={replaceTypeTo} options={types} placeholder="새 품사" onChange={setReplaceTypeTo}/>
          </div></div>
        </>}
        <div className="form__form-group"><span className="form__form-group-label">단어 업데이트 내역</span><div className="form__form-group-field col-9"><div className="bulk-word-log-options">
          <BulkWordCheckbox id="bulk-modify-log-ignore" label="이 작업을 숨깁니다. (추가/수정/삭제 횟수가 집계되지 않습니다)" checked={isUpdateLogIgnore} onChange={logIgnoreChanged}/>
          <BulkWordCheckbox id="bulk-modify-log-detail" label="이 작업의 세부 내용을 포함시킵니다." disabled={isUpdateLogIgnore} checked={isUpdateLogIncludeDetail} onChange={() => setUpdateLogIncludeDetail(!isUpdateLogIncludeDetail)}/>
        </div></div></div>
        <ButtonToolbar className="form__button-toolbar"><Button type="button" color="warning" onClick={handlePreview} disabled={isSaving}>{isSaving ? '확인 중...' : '변경 사항 미리보기'}</Button>
          <Button type="button" onClick={() => navigate(`/words-${lang}`)} disabled={isSaving}>작업 취소</Button></ButtonToolbar>
      </form>
    </CardBody></Card></Col>
    <BulkWordModifyConfirmModal isOpen={isOpenModal} lang={lang} preview={preview} request={request}
      onToggle={() => setOpenModal(!isOpenModal)} onShowToast={showToast} onComplete={() => navigate(`/words-${lang}`)}/>
  </>;
};

export default BulkWordModifyPage;
